import { CSSProperties, useState } from 'react';
import { clsx, makeKey, styx } from 'modules/Utils';
import useScreen from 'Hooks/useScreen';
import PageSymphony from 'elements/PageSymphony';
import CardSymphony from 'elements/CardSymphony';
import InputSymphony from 'elements/InputSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import Loader from 'elements/Loader';
import Banner from 'elements/Banner';
import TagSymphony from 'elements/TagSymphony';
import Icon from 'elements/Icon';
import SwitchSymphony from 'elements/SwitchSymphony';
import { 
    syncStripeSubscriptions_api, 
    SyncStripeSubscriptionsParams,
    SyncStripeSubscriptionsResponse,
    SyncedBrandInfo
} from 'api/adminApi';
import { toastError, toastSuccess } from 'modules/Toast';
import Colors from 'modules/Colors';
import Sty from 'modules/Sty';

const StripeSubscriptionSync = () => {
    const { mobileView } = useScreen();

    // State for sync operation
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [syncResponse, setSyncResponse] = useState<SyncStripeSubscriptionsResponse | null>(null);

    // Form states
    const [dryRun, setDryRun] = useState<boolean>(true);
    const [brandId, setBrandId] = useState<string>('');
    const [subscriptionId, setSubscriptionId] = useState<string>('');

    // Run sync operation
    const handleRunSync = async () => {
        setIsLoading(true);
        setSyncResponse(null);

        try {
            const params: SyncStripeSubscriptionsParams = {
                dryRun
            };

            if (brandId.trim()) {
                const parsedBrandId = parseInt(brandId.trim());
                if (!isNaN(parsedBrandId)) {
                    params.brandId = parsedBrandId;
                } else {
                    toastError('Brand ID must be a valid number');
                    setIsLoading(false);
                    return;
                }
            }

            if (subscriptionId.trim()) {
                params.stripe_subscription_id = subscriptionId.trim();
            }

            const response = await syncStripeSubscriptions_api(params);

            if (response) {
                setSyncResponse(response);
                toastSuccess(response.message);
            } else {
                toastError('Failed to sync Stripe subscriptions');
            }
        } catch (error) {
            console.error('Error running Stripe sync:', error);
            toastError('An error occurred while syncing subscriptions');
        } finally {
            setIsLoading(false);
        }
    };

    // Clear form and results
    const handleClear = () => {
        setBrandId('');
        setSubscriptionId('');
        setDryRun(true);
        setSyncResponse(null);
    };

    // Render sync form
    const renderSyncForm = () => {
        return (
            <CardSymphony
                className="stripeSync"
                title="Stripe Subscription Sync"
                style={sty.syncCard}
            >
                <div className="syncContainer" style={sty.syncContainer}>
                    <div className="syncDescription" style={sty.syncDescription}>
                        <p style={Sty.text14grey}>
                            Sync Stripe subscription data with the database for starter/growth plans. 
                            Only updates brands currently on the FREE tier to prevent overwriting existing subscriptions.
                        </p>
                    </div>

                    <div className="syncOptions" style={sty.syncOptions}>
                        <SwitchSymphony
                            className="dryRunSwitch"
                            label="Dry Run (Preview changes without applying them)"
                            checked={dryRun}
                            onChange={setDryRun}
                            labelPlacement="right"
                        />
                    </div>

                    <div className="filterInputs" style={sty.filterInputs}>
                        <InputSymphony
                            className="brandIdInput"
                            placeholder="Brand ID (optional)"
                            value={brandId}
                            onChange={setBrandId}
                            style={sty.filterInput}
                        />
                        <InputSymphony
                            className="subscriptionIdInput"
                            placeholder="Stripe Subscription ID (optional)"
                            value={subscriptionId}
                            onChange={setSubscriptionId}
                            style={sty.filterInput}
                        />
                    </div>

                    <div className="syncButtons" style={sty.syncButtons}>
                        <ButtonSymphony
                            className="runSyncButton"
                            onClick={handleRunSync}
                            isLoading={isLoading}
                            disabled={isLoading}
                        >
                            Run Sync
                        </ButtonSymphony>
                        <ButtonSymphony
                            className="clearButton"
                            variant="outlined"
                            onClick={handleClear}
                            disabled={isLoading}
                        >
                            Clear
                        </ButtonSymphony>
                    </div>
                </div>
            </CardSymphony>
        );
    };

    // Render brand row
    const renderBrandRow = (brand: SyncedBrandInfo, index: number) => {
        // Calculate if this brand was actually updated by comparing tiers
        const wasUpdated = brand.oldTier !== brand.newTier;
        const statusColor = wasUpdated ? 'green' : 'grey';

        return (
            <div
                key={makeKey('synced-brand', brand.brandId, index)}
                className="brandRow"
                style={sty.brandRow}
            >
                <div className="brandBasicInfo" style={sty.brandBasicInfo}>
                    <div className="brandIdAndName" style={sty.brandIdAndName}>
                        <div className="brandId" style={Sty.text16bold}>
                            Brand #{brand.brandId}
                        </div>
                        {brand.brandName && (
                            <div className="brandName" style={sty.brandName}>
                                <span style={Sty.text14}>{brand.brandName}</span>
                            </div>
                        )}
                    </div>
                    <div className="brandTags" style={sty.brandTags}>
                        <TagSymphony
                            className="statusTag"
                            title={wasUpdated ? `Updated: ${brand.oldTier} → ${brand.newTier}` : 'No Change'}
                            color={statusColor as any}
                            size="small"
                        />
                        <TagSymphony
                            className="tierTag"
                            title={`${brand.oldTier} → ${brand.newTier}`}
                            size="small"
                            color="blue"
                        />
                        <TagSymphony
                            className="billingTag"
                            title={brand.recurringInterval}
                            size="small"
                            color="purple"
                        />
                    </div>
                </div>
                <div className="brandMetadata" style={sty.brandMetadata}>
                    <div className="metadataItem" style={sty.metadataItem}>
                        <span style={Sty.text12grey}>Stripe Customer: </span>
                        <span style={Sty.text12}>{brand.stripeCustomerId}</span>
                    </div>
                    <div className="metadataItem" style={sty.metadataItem}>
                        <span style={Sty.text12grey}>Subscription: </span>
                        <span style={Sty.text12}>{brand.stripeSubscriptionId}</span>
                    </div>
                </div>
            </div>
        );
    };

    // Render sync results
    const renderSyncResults = () => {
        if (!syncResponse) {
            return null;
        }

        return (
            <CardSymphony
                className="syncResults"
                title="Sync Results"
                style={sty.resultsCard}
            >
                <div className="resultsContainer" style={sty.resultsContainer}>
                    {/* Summary */}
                    <div className="resultsSummary" style={sty.resultsSummary}>
                        <h3 style={{...Sty.text18, fontWeight: 'bold', marginBottom: 12}}>
                            Summary {syncResponse.isDryRun ? '(Dry Run)' : ''}
                        </h3>
                        <div className="summaryGrid" style={sty.summaryGrid}>
                            <div className="summaryItem" style={sty.summaryItem}>
                                <span style={Sty.text14grey}>Subscriptions Processed:</span>
                                <span style={Sty.text14bold}>{syncResponse.summary.totalSubscriptionsProcessed}</span>
                            </div>
                            <div className="summaryItem" style={sty.summaryItem}>
                                <span style={Sty.text14grey}>Brands Examined:</span>
                                <span style={Sty.text14bold}>{syncResponse.summary.brandsExamined}</span>
                            </div>
                            <div className="summaryItem" style={sty.summaryItem}>
                                <span style={Sty.text14grey}>
                                    {syncResponse.isDryRun ? 'Would Update:' : 'Updated:'}
                                </span>
                                <span style={Sty.text14bold}>{syncResponse.summary.brandsUpdated}</span>
                            </div>
                            <div className="summaryItem" style={sty.summaryItem}>
                                <span style={Sty.text14grey}>Errors:</span>
                                <span style={{...Sty.text14bold, color: syncResponse.summary.errorsEncountered > 0 ? Colors.red : Colors.green}}>
                                    {syncResponse.summary.errorsEncountered}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Message */}
                    <Banner
                        className="syncMessage"
                        title="Sync Complete"
                        description={syncResponse.message}
                        color={syncResponse.isDryRun ? 'blue' : 'green'}
                    />

                    {/* Updated Brands */}
                    {syncResponse.updatedBrands.length > 0 && (
                        <div className="updatedBrands" style={sty.updatedBrands}>
                            <h3 style={{...Sty.text16, fontWeight: 'bold', marginBottom: 12}}>
                                {syncResponse.isDryRun ? 'Brands That Would Be Updated' : 'Updated Brands'} ({syncResponse.updatedBrands.length})
                            </h3>
                            <div className="brandsContainer" style={sty.brandsContainer}>
                                {syncResponse.updatedBrands.map(renderBrandRow)}
                            </div>
                        </div>
                    )}

                    {/* Errors */}
                    {syncResponse.errors.length > 0 && (
                        <div className="syncErrors" style={sty.syncErrors}>
                            <h3 style={{...Sty.text16, fontWeight: 'bold', marginBottom: 12, color: Colors.red}}>
                                Errors ({syncResponse.errors.length})
                            </h3>
                            <div className="errorsContainer" style={sty.errorsContainer}>
                                {syncResponse.errors.map((error, index) => (
                                    <div
                                        key={makeKey('sync-error', index)}
                                        className="errorItem"
                                        style={sty.errorItem}
                                    >
                                        {error.brandId && (
                                            <span style={Sty.text12bold}>Brand #{error.brandId}: </span>
                                        )}
                                        <span style={Sty.text12}>{error.error}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Raw JSON Response */}
                    <div className="rawResponse" style={sty.rawResponse}>
                        <h3 style={{...Sty.text16, fontWeight: 'bold', marginBottom: 12}}>
                            Raw JSON Response
                        </h3>
                        <div className="jsonContainer" style={sty.jsonContainer}>
                            <pre style={sty.jsonPre}>
                                {JSON.stringify(syncResponse, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>
            </CardSymphony>
        );
    };

    return (
        <PageSymphony className="stripeSubscriptionSyncPage">
            <div className="stripeSubscriptionSyncPageContent" style={sty.pageContent}>
                <div className="stripeSubscriptionSyncHeader" style={sty.header}>
                    <div className="headerContent" style={sty.headerContent}>
                        <div className="headerText" style={sty.headerText}>
                            <h1 style={{...Sty.text32, fontWeight: 'bold'}}>Stripe Subscription Sync</h1>
                            <p style={Sty.text16grey}>
                                Sync Stripe subscription data with the database
                            </p>
                        </div>
                    </div>
                </div>

                {renderSyncForm()}
                {renderSyncResults()}
            </div>
        </PageSymphony>
    );
};

const sty: Record<string, CSSProperties> = {
    pageContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        maxWidth: 1200,
        margin: '0 auto',
        padding: 24
    },
    header: {
        marginBottom: 16
    },
    headerContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24
    },
    headerText: {
        textAlign: 'center',
        flex: 1
    },
    syncCard: {
        backgroundColor: Colors.white
    },
    syncContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20
    },
    syncDescription: {
        padding: 16,
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        border: `1px solid ${Colors.grey}`
    },
    syncOptions: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12
    },
    filterInputs: {
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap'
    },
    filterInput: {
        flex: 1,
        minWidth: 250
    },
    syncButtons: {
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end'
    },
    resultsCard: {
        backgroundColor: Colors.white
    },
    resultsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24
    },
    resultsSummary: {
        padding: 16,
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        border: `1px solid ${Colors.grey}`
    },
    summaryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16
    },
    summaryItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    updatedBrands: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12
    },
    brandsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxHeight: 400,
        overflowY: 'auto',
        border: `1px solid ${Colors.greyLight}`,
        borderRadius: 8
    },
    brandRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 16,
        borderBottom: `1px solid ${Colors.greyLight}`,
        backgroundColor: Colors.white
    },
    brandBasicInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        flex: 1
    },
    brandIdAndName: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4
    },
    brandName: {
        display: 'flex',
        alignItems: 'center',
        gap: 4
    },
    brandTags: {
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap'
    },
    brandMetadata: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        minWidth: 300
    },
    metadataItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 4
    },
    syncErrors: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12
    },
    errorsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        maxHeight: 200,
        overflowY: 'auto',
        padding: 12,
        backgroundColor: Colors.redTransparent,
        borderRadius: 8,
        border: `1px solid ${Colors.redLight}`
    },
    errorItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 4
    },
    rawResponse: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12
    },
    jsonContainer: {
        maxHeight: 400,
        overflowY: 'auto',
        border: `1px solid ${Colors.greyLight}`,
        borderRadius: 8,
        backgroundColor: Colors.greyLight
    },
    jsonPre: {
        margin: 0,
        padding: 16,
        fontSize: 12,
        fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word'
    }
};

export default StripeSubscriptionSync; 